/** Reviewed worker residue, served as data rather than synthesized candles. */
export interface ResidueEntry {
  readonly symbol: "ES1!";
  readonly minute: string;
  readonly reason: "SOURCE_ABSENT";
  readonly ruling: string;
  readonly state: "ABSENT" | "FILLED";
  readonly filled_by: string | null;
}

export type VwapDisplayState = "healthy" | "stale" | "error" | "no-data";

export function vwapDisplayState(input: {
  feed: { stale?: boolean | null } | null;
  error: string | null;
  failures: number;
}): VwapDisplayState {
  if (input.failures >= 3 || (input.error !== null && input.feed === null)) return "error";
  if (input.feed === null) return "no-data";
  if (input.feed.stale === true) return "stale";
  return "healthy";
}

export function wallDisplayState(
  response: { ok?: boolean; state?: string; reason?: string } | null,
  transportError: string | null,
): "healthy" | "stale" | "no-data" | "error" {
  const state = response?.state?.toUpperCase() ?? "";
  const sentence = `${response?.reason ?? ""} ${transportError ?? ""}`.toLowerCase();
  const explicitEmpty = state === "EMPTY" || /no live .*ladder|refusing to fabricate/.test(sentence);
  if (explicitEmpty) return "no-data";
  if (transportError !== null) return "error";
  if (response?.ok === false) return "no-data";
  if (response?.state === "STALE") return "stale";
  return "healthy";
}

export function markerGapLabel(count: number, timestamp: number | null, closed = false): string {
  const noun = `CANDLE GAP${count === 1 ? "" : "S"}`;
  const when = timestamp === null ? "timestamp unavailable" : new Date(timestamp).toISOString();
  return `MARKERS ${closed ? "CLOSED" : "ERROR"} · ${count} ${noun} · ${when}`;
}

export interface MarkerGapState {
  label: string;
  alert: boolean;
  logError: boolean;
}

/** One CLOSED decision feeds the marker pill and its log arm. */
export function markerGapState(
  count: number,
  timestamp: number | null,
  venueClosed = false,
  verdictKnown = true,
): MarkerGapState | null {
  if (count <= 0) return null;
  return {
    label: markerGapLabel(count, timestamp, venueClosed),
    alert: !venueClosed,
    logError: verdictKnown && !venueClosed,
  };
}

type ChipInkState = "error" | "nodata" | "closed" | "reopening" | "stale" | "live";

/** Preserve feedState truth while CLOSED replaces only error/no-data ink. */
export function closedVenueChipInkState(raw: ChipInkState, servedVenueState: string | null): ChipInkState {
  return servedVenueState === "CLOSED" && (raw === "error" || raw === "nodata") ? "closed" : raw;
}

export type LastSessionFreshness = "CHECKED" | "STALE" | "CLOSED";
export interface ServedVenueClock { verdict?: string | null; last_session_open?: string | null }

const NY_DATE = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" });

/**
 * ORDER_HUD_GEX_GRID_CLOSED_1 R2: the worker's stale word beside the door's own venue. Data dated the last session
 * at a CLOSED venue reads CLOSED; a day-old date, a missing date or an unserved venue clock stays STALE.
 */
export function lastSessionFreshness(
  stale: boolean, venue: ServedVenueClock | null | undefined, dataDates: readonly (string | null)[],
): LastSessionFreshness {
  if (!stale) return "CHECKED";
  const open = venue?.verdict === "CLOSED" ? Date.parse(venue.last_session_open ?? "") : Number.NaN;
  if (!Number.isFinite(open) || !dataDates.length) return "STALE";
  const session = NY_DATE.format(open);
  return dataDates.every((d) => d === session) ? "CLOSED" : "STALE";
}
